import { Icon } from "@focus/focusUI";
import { EventObjectPair } from "./EventObjectPair";
import { EventRecordDetails } from "./EventRecordDetails";
import type { EventEvolutionEvent } from "./EventEvolutionTypes";
import { withTimeSystem } from "../../constants/orbitMetadata";
import type { FormatterMap } from "../../helpers/utils";
import { formatDate } from "./eventEvolutionUtils";
import type { ConjunctionRiskConfig } from "../conjunctions/conjunctionUiUtils";
import {
  getDisplayValue,
  getRiskLabel,
  getRiskTone,
} from "../conjunctions/conjunctionUiUtils";

export type { EventEvolutionEvent } from "./EventEvolutionTypes";

interface EventEvolutionCardProps {
  event?: EventEvolutionEvent;
  formatters?: FormatterMap;
  riskConfig?: ConjunctionRiskConfig;
}

export const EventEvolutionCard = ({
  event,
  formatters,
  riskConfig,
}: EventEvolutionCardProps) => {
  const riskCandidates = [
    event?.riskLevel,
    ...(event?.conjunctions?.map((conjunction) => conjunction?.riskLevel) ??
      []),
  ];

  const riskRank = (value: unknown) => {
    const risk = getDisplayValue(value).toUpperCase();

    if (risk === "ALERT") return 2;
    if (risk === "WARN" || risk === "WARNING") return 1;

    return 0;
  };

  const riskLevel =
    riskCandidates.reduce<unknown>(
      (current, candidate) =>
        riskRank(candidate) > riskRank(current) ? candidate : current,
      undefined,
    ) ?? event?.riskLevel ?? event?.conjunctions?.[0]?.riskLevel;
  const riskTone = getRiskTone(riskLevel);
  const riskLabel = riskLevel ? getRiskLabel(riskLevel) : "Info";
  const hasRisk = riskTone !== "muted" || Boolean(riskLevel);

  return (
    <aside className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="shrink-0 border-b border-slate-200 bg-slate-50 px-5 py-4">
        <div className="flex items-start gap-3">
          {hasRisk ? (
            <div
              className={`flex size-11 shrink-0 items-center justify-center rounded-xl shadow-sm ${
                riskTone === "danger"
                  ? "bg-red-600 text-white"
                  : riskTone === "warning"
                    ? "bg-amber-500 text-white"
                    : "bg-slate-300 text-slate-700"
              }`}
            >
              <Icon icon="warning-sign" size={24} />
            </div>
          ) : (
            <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-400 shadow-sm">
              <Icon icon="info-sign" size={24} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${
                  riskTone === "danger"
                    ? "bg-red-100 text-red-700"
                    : riskTone === "warning"
                      ? "bg-amber-100 text-amber-700"
                      : "bg-slate-100 text-slate-600"
                }`}
              >
                {riskLabel}
              </span>
              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                {`${withTimeSystem("Execution Epoch")}: ${formatDate(
                  event?.executionTime,
                )}`}
              </span>
            </div>
            <div className="mt-3">
              <EventObjectPair event={event} />
            </div>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-5 pr-4">
        <EventRecordDetails
          event={event}
          formatters={formatters}
          riskConfig={riskConfig}
        />
      </div>
    </aside>
  );
};
